import { css } from '@emotion/react';
import { useState } from 'react';

import { ApiScan } from '@kaiizen/kai-pro-api';
import { useFeatureFlags, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

import { isOptedIntoNewExperience } from '@/Client/Lib/newExperienceLib';
import { AIScanVideoUploadModal, AIScanVideoUploadTriggerButton, ArtifactStatusCard } from '@/Client/Partials';
import { useAIScanVideoActiveWork } from '@/Client/Partials/AIScanVideoUploadModal/useAIScanVideoActiveWork';
import { type ScanUrlParts, useNavigateToScan } from '@/Client/State';
import { LAYOUT2 } from '@/Client/Styles';
import { LabeledCard } from '@/Client/Widgets';

const STATUS_COLUMNS_ROW_STYLE = css`
  label: STATUS_COLUMNS_ROW_STYLE;
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing1}px;
`;

/** Resolves whether the Close Out status card opens the historical artifact or the review flow. */
export const getCloseoutStatusCardNavigation = ({
  firstApprovedCloseoutScanId,
  latestScanId,
  newExperience,
}: {
  firstApprovedCloseoutScanId?: string;
  latestScanId: string;
  newExperience: boolean;
}): ScanUrlParts =>
  newExperience && firstApprovedCloseoutScanId
    ? {
      scanId: latestScanId,
      focusPanel: 'artifact',
      artifactScanId: firstApprovedCloseoutScanId,
      artifactDocument: 'closeout',
    }
    : { scanId: latestScanId, page: 'closeout' };

export const PrimaryProjectArtifactStatusCards = ({ scans }: { scans: ApiScan[] }) => {
  const { featureFlags } = useFeatureFlags();
  const [aiVideoUploadModalOpen, setAiVideoUploadModalOpen] = useState(false);
  const {
    project,
    latestScan,
    latestApprovedScan,
    firstApprovedCloseoutScan,
    projectIsEditable,
    scanHasScopeChanges,
    scanHasTasksNeedingRework,
    scanIsCloseout,
  } = useScanEditingContextSlice([
    'project',
    'latestScan',
    'latestApprovedScan',
    'firstApprovedCloseoutScan',
    'projectIsEditable',
    'baselineScanWasCloseOut',
    'scanHasScopeChanges',
    'scanHasTasksNeedingRework',
    'scanIsCloseout',
  ]);
  const navigateToScan = useNavigateToScan();

  const aiScanVideoUploadContextEnabled = !!latestScan
    && !!project
    && projectIsEditable
    && featureFlags.useAiVideoScan
    && !latestApprovedScan;

  const showUploadWalkVideoButton = aiScanVideoUploadContextEnabled
    && latestScan.scanLifecycleStatus === 'dispatched';

  const {
    activeCount,
    items: activeWorkItems,
    hasFetchedOnce: activeWorkFetched,
    refetch: refetchAiScanVideoActiveWork,
  } = useAIScanVideoActiveWork(
    latestScan?.id ?? '',
    aiScanVideoUploadContextEnabled,
  );

  if (!latestScan || !project) return null;

  const scopeCardActive = !scanIsCloseout;
  const changeOrderActive = scanHasScopeChanges;
  const punchListActive = scanHasTasksNeedingRework;
  const closeOutActive = scanIsCloseout;

  const isAddendum = firstApprovedCloseoutScan && latestScan?.parentScanId !== firstApprovedCloseoutScan?.id;

  return (
    <LabeledCard title='CURRENT DOCUMENTS'>
      <div css={STATUS_COLUMNS_ROW_STYLE}>
        <ArtifactStatusCard
          type='scope'
          title='Scope'
          buttonTitle={latestApprovedScan ? 'View Scope' : latestScan.scanLifecycleStatus === 'created'
            ? 'Dispatch Initial Walk'
            : latestScan.scanLifecycleStatus === 'dispatched'
            ? 'Start Scope'
            : latestScan.scanLifecycleStatus === 'pendingReview' || latestScan.scanLifecycleStatus === 'inReview'
            ? 'Review Scope'
            : 'Edit Scope'}
          action={scopeCardActive && (() => {
            if (featureFlags.newScopeBuilderFlow) {
              // we now ALWAYS navigate to the review page for the scope card b/c the reportType
              // pages will cease to exist in the future
              navigateToScan({ scanId: latestScan.id, page: 'review' });
            } else {
              navigateToScan({ scanId: latestScan.id, page: latestApprovedScan ? 'scope' : undefined });
            }
          })}
          secondaryButtonContent={showUploadWalkVideoButton
            ? (
              <AIScanVideoUploadTriggerButton
                variant={'projectCard'}
                activeCount={activeCount}
                buttonStyle={'yellow'}
                flex={1}
                onClick={() => setAiVideoUploadModalOpen(true)}
              />
            )
            : undefined}
          isAddendum={scopeCardActive && isAddendum}
        />
        <ArtifactStatusCard
          type='changeOrder'
          title='Change Order'
          buttonTitle={'Edit Change Order'}
          action={changeOrderActive ? () => navigateToScan({ scanId: latestScan.id, page: 'changeOrder' }) : undefined}
          isAddendum={changeOrderActive && isAddendum}
        />
        <ArtifactStatusCard
          type='punchList'
          title='Punch List'
          buttonTitle={'View Punch List'}
          action={punchListActive ? () => navigateToScan({ scanId: latestScan.id, page: 'punchList' }) : undefined}
        />
        <ArtifactStatusCard
          type='progress'
          title='Close Out'
          buttonTitle={projectIsEditable ? 'Review Close Out' : 'View Close Out'}
          action={closeOutActive
            ? () =>
              navigateToScan(getCloseoutStatusCardNavigation({
                firstApprovedCloseoutScanId: firstApprovedCloseoutScan?.id,
                latestScanId: latestScan.id,
                // The artifact route only exists for users opted into the new experience;
                // the flag alone would send everyone else to a 404.
                newExperience: isOptedIntoNewExperience(featureFlags),
              }))
            : undefined}
          isAddendum={closeOutActive && isAddendum}
        />
      </div>
      <AIScanVideoUploadModal
        visible={aiVideoUploadModalOpen}
        onClose={() => {
          setAiVideoUploadModalOpen(false);
          void refetchAiScanVideoActiveWork();
        }}
        scanId={latestScan.id}
        serverActiveWork={{ items: activeWorkItems, hasFetchedOnce: activeWorkFetched }}
      />
    </LabeledCard>
  );
};
